import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Trash2, Copy } from 'lucide-react';

const AdminImageManage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
      return;
    }
    fetchImages();
  }, [user, navigate]);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/images`);
      setImages(response.data.images);
    } catch (error) {
      toast.error('無法載入圖片');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('上傳成功');
      fetchImages();
    } catch (error) {
      toast.error('上傳失敗');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('確定要刪除嗎？')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/images/${imageId}`);
      toast.success('刪除成功');
      fetchImages();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };

  const copyUrl = (imageId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/images/${imageId}`;
    navigator.clipboard.writeText(url);
    toast.success('已複製 URL');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="admin-image-manage">
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold">圖片管理</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <label className="glass p-8 rounded-3xl block cursor-pointer hover:shadow-xl transition-all text-center" data-testid="upload-area">
            <Upload className="w-12 h-12 mx-auto mb-4 text-primary" />
            <p className="text-lg font-semibold mb-2">
              {uploading ? '上傳中...' : '點擊上傳圖片'}
            </p>
            <p className="text-sm text-slate-600">支持 JPG, PNG, GIF 等格式</p>
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading}
              data-testid="file-input"
            />
          </label>
        </div>

        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
          {images.map((image) => (
            <div key={image.image_id} className="bg-white rounded-2xl overflow-hidden shadow-sm" data-testid={`image-item-${image.image_id}`}>
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/images/${image.image_id}`}
                alt={image.filename}
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <p className="text-sm text-slate-600 truncate mb-2">{image.filename}</p>
                <p className="text-xs text-slate-400 mb-3">
                  {new Date(image.uploaded_at).toLocaleDateString('zh-HK')}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => copyUrl(image.image_id)} className="flex-1" data-testid={`copy-btn-${image.image_id}`}>
                    <Copy className="w-3 h-3 mr-1" />
                    複製
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(image.image_id)} data-testid={`delete-btn-${image.image_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {images.length === 0 && (
          <div className="text-center text-slate-600 py-16">
            <p className="text-lg">目前沒有圖片，請上傳圖片</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminImageManage;